// /analyze flow: upload with X-Analyze-Async: 1, get a job_id back, poll
// GET /analyze/{job_id} until the worker finishes. Everything here resolves to the
// response-body shape finishAnalysis() renders (the same shape the old synchronous
// endpoint returned).

const PENDING_KEY = "flowline.pendingAnalysisJob";

const POLL_INTERVAL_MS = 3000;
const RETRY_DELAYS_MS = [3000, 6000, 10000, 10000, 10000]; // ~40s of consecutive misses before giving up
const MAX_WAIT_MS = 20 * 60 * 1000;

const CONNECTION_ERROR = {
  error: true,
  message: "We lost the connection while checking on your analysis. Reload the page to pick up where you left off.",
};

// `answers` is non-null when the questionnaire was already submitted before the upload
// returned a job id (a slow upload), so a reload still skips straight to the waiting screen.
export function savePendingJob(userId, jobId, answers = null) {
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify({ userId, jobId, answers }));
  } catch {}
}

// Called when the questionnaire is submitted, so a reload after that point doesn't ask again.
export function savePendingAnswers(answers) {
  try {
    const pending = JSON.parse(localStorage.getItem(PENDING_KEY));
    if (pending) localStorage.setItem(PENDING_KEY, JSON.stringify({ ...pending, answers }));
  } catch {}
}

export function loadPendingJob(userId) {
  try {
    const pending = JSON.parse(localStorage.getItem(PENDING_KEY));
    return pending && pending.userId === userId && pending.jobId ? pending : null;
  } catch {
    return null;
  }
}

export function clearPendingJob() {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {}
}

const defaultSleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Upload and enqueue. Returns { jobId } on 202, otherwise the response body in the same
// shape the sync path produces (limit_reached, enqueue_failed, job_in_progress, ...).
export async function enqueueAnalysis(apiUrl, file, token) {
  const formData = new FormData();
  formData.append("file", file);
  const res = await fetch(`${apiUrl}/analyze`, {
    method: "POST",
    body: formData,
    headers: { Authorization: `Bearer ${token}`, "X-Analyze-Async": "1" },
  });
  const data = await res.json();
  if (res.status === 202 && data.job_id) return { jobId: data.job_id };
  return { result: { ...data, _status: res.status } };
}

// Poll until the job is done or failed. A single failed request (network error, 5xx, 429)
// is retried with backoff; only a run of consecutive misses gives up. Never throws.
// Result has `terminal: true` when the job itself is finished (done/failed/gone), so the
// caller knows whether a reload could still pick it up.
export async function pollAnalysisJob(apiUrl, jobId, getToken, { onStatus, sleep = defaultSleep, now = Date.now } = {}) {
  const started = now();
  let misses = 0;

  while (true) {
    if (now() - started > MAX_WAIT_MS) {
      return { terminal: false, data: { error: true, message: "Your analysis is taking longer than expected. Reload the page later to check on it." } };
    }

    let res = null;
    let body = null;
    try {
      const token = await getToken();
      if (!token) {
        return { terminal: false, data: { error: true, message: "Your session has expired. Please sign in again." } };
      }
      res = await fetch(`${apiUrl}/analyze/${encodeURIComponent(jobId)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      body = await res.json();
    } catch {
      res = null;
    }

    if (res && res.ok && body) {
      misses = 0;
      onStatus && onStatus(body.status);
      if (body.status === "done") {
        return { terminal: true, data: { ...body.result, _status: 200 } };
      }
      if (body.status === "failed") {
        return { terminal: true, data: { error: true, message: body.error || "The analysis did not complete. Please try again." } };
      }
      // queued / running
      await sleep(POLL_INTERVAL_MS);
      continue;
    }

    if (res && res.status === 404) {
      return { terminal: true, data: { error: true, message: "We couldn't find that analysis. Please upload your video again." } };
    }
    if (res && res.status === 401) {
      return { terminal: false, data: { error: true, message: "Your session has expired. Please sign in again." } };
    }

    // Network error, 5xx, 429, or an unparseable body: transient, retry.
    if (misses >= RETRY_DELAYS_MS.length) {
      return { terminal: false, data: CONNECTION_ERROR };
    }
    await sleep(RETRY_DELAYS_MS[misses]);
    misses += 1;
  }
}
